import React, { useCallback, useState, useMemo } from 'react';
import { Button } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import {
  individual_column_style,
  individual_header_style,
  icon_column_style,
  individual_icon_header_style
} from '../../../../../../../../assets/styles/table_custom_style';

import ModalConfirmation from 'components/Modal/ConfirmationModal';

import LeadService from '../../../../../services/leadService';
import { toast } from 'react-toastify';

import './styles.css';
import { toBrazilianFullDate } from 'views/CRM/shared/utils/date';
import LeadHistoryModal from '../../../LeadHistoryModal';
import { maskCpf } from 'views/CRM/shared/utils/cpfCnpjHelpers';
import { maskCnpj } from 'views/CRM/shared/utils/cpfCnpjHelpers';
import { formatCurrency } from 'utils/fuctions/formatCurrency';
import { verifyRoles } from 'components/Authentication/VerifyRoles';
import LeadActionsModal from '../../../LeadActionsModal';
import ModalLeadInfo from '../ModalLeadInfo';
import FlexTable from 'components/FlexTable';
import { PhoneCell } from 'components/FlexTable/components/cells/phone';

const ListLeadsTable = ({
  leads,
  reloadLeads,
  customFields,
  setSortConfig,
  sortConfig,
  selectedRows,
  handleOnSelect,
  handleOnSelectAll,
  transferLeads,
  openModalLeadId = null
}) => {
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [leadId, setLeadId] = useState(null);

  const history = useHistory();

  const handleDeleteLead = useCallback(
    (id) => {
      LeadService.delete(id)
        .catch((err) => {
          const message =
            err?.response?.data?.message || 'Erro ao deletar o lead';
          toast.error(message);
        })
        .finally(() => {
          setOpenModalConfirmation(false);
          reloadLeads(1);
        });
    },
    [reloadLeads]
  );

  const editLead = (lead) => {
    history.push(`/admin/crm/leadEdit/${lead.recordTypeId}/${lead.id}`);
  };

  const controlDeleteLead = (id) => {
    setLeadId(id);
    setOpenModalConfirmation(true);
  };

  const dynamicFunctionColumns = useMemo(() => {
    const columns = [];

    columns.push(
      {
        dataField: 'name',
        text: 'Nome',
        sort: true,
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_, row) => {
          return (
            <>
              {!row.isConverted && verifyRoles(['CRM_LEADS_EDIT']) ? (
                <span
                  className="lead-name"
                  onClick={() => {
                    editLead(row);
                  }}
                >
                  {row.name}
                </span>
              ) : (
                <span>{row.name}</span>
              )}
            </>
          );
        }
      },
      {
        dataField: 'phone',
        text: 'Telefone',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_, row) => {
          if (!row?.phone) return null;
          return <PhoneCell phone={row.phone} ddi={row?.ddi} />;
        }
      },
      {
        dataField: 'stage_name',
        text: 'Fase',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { stageName }) => stageName
      },
      {
        dataField: 'created_at',
        text: 'Criado em',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { createdAt }) => toBrazilianFullDate(createdAt)
      },
      {
        dataField: 'created_by',
        text: 'Criado por',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { createdByName }) => createdByName
      },
      {
        dataField: 'responsible',
        text: 'Responsável',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { responsibleName }) => responsibleName
      },
      {
        dataField: 'last_updated_at',
        text: 'Última atualização',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { lastUpdatedAt }) =>
          toBrazilianFullDate(lastUpdatedAt)
      },
      {
        dataField: 'catalogName',
        text: 'Catálogo de preço',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { catalogName }) => catalogName
      },
      {
        dataField: 'productName',
        text: 'Produto',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { productName }) => productName
      },
      {
        dataField: 'catalogTotalValue',
        text: 'Valor total',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { catalogTotalValue }) =>
          formatCurrency(catalogTotalValue, false)
      },
      {
        dataField: 'channelOrigin',
        text: 'Origem da Interação',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { channelOrigin }) => channelOrigin || '-'
      },
      {
        dataField: 'firstContactDate',
        text: 'Data do Primeiro Contato',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { firstContactDate }) =>
          firstContactDate ? toBrazilianFullDate(firstContactDate) : '-'
      },
      {
        dataField: 'isConverted',
        text: 'Gerou Oportunidade',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { isConverted }) => (isConverted ? 'Sim' : 'Não')
      },
      {
        dataField: 'isManual',
        text: 'Gerado Manualmente',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { isManual }) => (isManual ? 'Sim' : 'Não')
      },
      {
        dataField: 'associatedBy',
        text: 'Operador que Associou',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { associatedBy }) => associatedBy
      },
      {
        dataField: 'associationDate',
        text: 'Data de Associação',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { associationDate }) =>
          associationDate ? toBrazilianFullDate(associationDate) : '-'
      }
    );

    (customFields || []).forEach((field) => {
      columns.push({
        dataField: field.name,
        text: field.label,
        style: individual_column_style,
        formatter: (_, row) => {
          if (field.type === 'DATE' && !!row[field.name]) {
            return new Date(row[field.name]).toLocaleDateString('pt-br');
          } else if (field.type === 'TELEPHONE' && row[field.name]) {
            return (
              <PhoneCell
                phone={row[field?.name].number}
                ddi={row[field?.name]?.ddi}
              />
            );
          } else if (field.type === 'CPF' && row[field.name]) {
            return maskCpf(row[field.name]);
          } else if (field.type === 'CNPJ' && row[field.name]) {
            return maskCnpj(row[field.name]);
          } else if (field.type === 'CURRENCY' && row[field.name]) {
            return formatCurrency(row[field.name]);
          }
          return row[`${field.name}`];
        }
      });
    });

    columns.push({
      dataField: 'edit',
      text: 'Editar',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <>
            {!row.isConverted && verifyRoles(['CRM_LEADS_EDIT']) ? (
              <Button size="sm" color="info" onClick={() => editLead(row)}>
                <li className="fas fa-user-edit"></li>
              </Button>
            ) : null}
          </>
        );
      }
    });

    columns.push({
      dataField: 'info',
      text: 'Informações',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <ModalLeadInfo
            lead={row}
            openAutomatically={openModalLeadId === row.id}
          />
        );
      }
    });

    columns.push({
      dataField: 'history',
      text: 'Histórico',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return <LeadHistoryModal leadId={row.id} />;
      }
    });

    columns.push({
      dataField: 'actions',
      text: 'Ações',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return <LeadActionsModal leadData={row} />;
      }
    });

    columns.push({
      dataField: 'delete',
      text: 'Excluir',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <>
            {!row.isConverted && verifyRoles(['DELETE_LEAD_CRM']) ? (
              <Button
                size="sm"
                color="danger"
                onClick={() => controlDeleteLead(row.id)}
              >
                <li className="fas fa-trash"></li>
              </Button>
            ) : null}
          </>
        );
      }
    });

    return [...columns];
  }, [openModalLeadId, customFields]);

  return (
    <>
      <ToolkitProvider
        data={leads || []}
        keyField="id"
        columns={dynamicFunctionColumns}
      >
        {(props) => (
          <div className="py-4 table-responsive">
            <FlexTable
              {...props.baseProps}
              classes="align-items-center"
              setSortConfig={setSortConfig}
              sortConfig={sortConfig}
              entityName="leads"
              selectRow={transferLeads}
              onSelect={handleOnSelect}
              onSelectAll={handleOnSelectAll}
              selectedRows={selectedRows}
            />
          </div>
        )}
      </ToolkitProvider>

      <ModalConfirmation
        isModalOpen={openModalConfirmation}
        onModalToggle={setOpenModalConfirmation}
        unmountOnClose={true}
        idResource={leadId}
        deleteResource={handleDeleteLead}
        message={'Tem certeza que deseja excluir o lead?'}
      />
    </>
  );
};

export default ListLeadsTable;
